import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import * as commerce from '@/services/commerceService'
import { fetchStripeConnect } from '@/services/stripeConnectApi'
import { fetchPaystackPayout } from '@/services/paystackPayoutApi'
import { effectiveWalletCurrency } from '@/lib/storeWallet'
import { useCurrentStore } from '@/context/CurrentStoreContext'
import { Card, CardHeader } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { SkeletonListPage } from '@/components/ui/Skeleton'
import { LoadMoreFooter } from '@/components/ui/LoadMoreFooter'
import { EmptyState } from '@/components/ui/EmptyState'
import { useLoadMoreList } from '@/hooks/useLoadMoreList'
import { getUserFacingErrorMessage } from '@/utils/userFacingError'

const DEFAULT_LIMIT = 25

function formatWithdrawalStatus(status: string): string {
  if (status === 'paid') return 'Disbursed'
  if (status === 'approved') return 'Approved'
  if (status === 'pending') return 'Pending'
  if (status === 'rejected') return 'Declined'
  return status
}

function formatWhen(iso: string) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return iso
  }
}

export default function WithdrawalsPage() {
  const { currentStoreId: storeId, storeRecord } = useCurrentStore()
  const [wallet, setWallet] = useState<commerce.WalletSummary | null>(null)
  const [walletLoading, setWalletLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [amount, setAmount] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [stripeReady, setStripeReady] = useState(false)
  const [paystackReady, setPaystackReady] = useState(false)

  const loadWallet = useCallback(async () => {
    if (storeId == null) {
      setWallet(null)
      setWalletLoading(false)
      setStripeReady(false)
      setPaystackReady(false)
      return
    }
    setWalletLoading(true)
    try {
      const [w, stripe, paystack] = await Promise.all([
        commerce.fetchStoreWallet(storeId, effectiveWalletCurrency(storeRecord)),
        fetchStripeConnect(storeId).catch(() => null),
        fetchPaystackPayout(storeId).catch(() => null),
      ])
      setWallet(w)
      setStripeReady(Boolean(stripe?.readyForCheckout))
      setPaystackReady(Boolean(paystack?.readyForPayout))
    } catch {
      setWallet(null)
      setStripeReady(false)
      setPaystackReady(false)
    } finally {
      setWalletLoading(false)
    }
  }, [storeId, storeRecord])

  useEffect(() => {
    void loadWallet()
  }, [loadWallet])

  const fetchPage = useCallback(
    async (page: number, limit: number) => {
      if (storeId == null) return { items: [], total: 0 }
      const res = await commerce.fetchWithdrawalRequests({ storeId, page, limit })
      return { items: res.items, total: res.total }
    },
    [storeId],
  )

  const {
    items: withdrawals,
    total: listTotal,
    loading: listLoading,
    loadingMore,
    error: listError,
    hasMore,
    reload,
    loadMore,
  } = useLoadMoreList<commerce.WithdrawalRequestRow>({
    fetchPage,
    limit: DEFAULT_LIMIT,
    resetDeps: [storeId],
  })

  function closeModal() {
    setModalOpen(false)
    setAmount('')
  }

  async function submitWithdrawal() {
    if (storeId == null) return
    const n = Number(amount)
    if (!Number.isFinite(n) || n <= 0) {
      setError('Enter a valid amount')
      return
    }
    const available = Number(wallet?.availableBalance ?? 0)
    if (n > available + 0.0001) {
      const pending = Number(wallet?.pendingSettlementBalance ?? 0)
      setError(
        pending > 0
          ? `You can only withdraw what’s Available right now (${available.toFixed(2)}). ${pending.toFixed(2)} is still on the way to your account.`
          : `Amount exceeds available balance (${available.toFixed(2)})`,
      )
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      await commerce.createWithdrawalRequest(storeId, {
        amount: n,
        ...(wallet?.currency ? { currency: wallet.currency } : {}),
      })
      setAmount('')
      setModalOpen(false)
      await Promise.all([loadWallet(), reload()])
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Withdrawal failed'))
    } finally {
      setSubmitting(false)
    }
  }

  const pageError = error || listError

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Withdrawals"
          description={
            stripeReady || paystackReady
              ? 'Request payouts from your wallet. Approved withdrawals are sent to your payout bank account via Kartly.'
              : 'Request payouts from your wallet balance and track every withdrawal request.'
          }
          action={
            <Button
              type="button"
              onClick={() => setModalOpen(true)}
              disabled={!storeId || walletLoading || Number(wallet?.availableBalance ?? 0) <= 0}
            >
              Request withdrawal
            </Button>
          }
        />

        {stripeReady || paystackReady ? (
          <p className="mb-4 text-sm text-muted-foreground">
            Funds move to your bank when an admin marks your request disbursed (or via{' '}
            <Link to="/payments/stripe" className="underline underline-offset-2">
              auto-withdraw
            </Link>
            ). There is no separate off-platform bank wire for these wallet credits.
          </p>
        ) : null}

        {pageError ? <p className="mb-4 text-sm text-destructive">{pageError}</p> : null}

        {walletLoading ? (
          <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="h-20 animate-pulse rounded-xl border border-border bg-muted/30" />
            ))}
          </div>
        ) : wallet ? (
          <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Available balance</p>
              <p className="mt-1 text-lg font-semibold tabular-nums">
                {wallet.currency} {wallet.availableBalance}
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">Ready to withdraw</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">On the way</p>
              <p className="mt-1 text-lg font-semibold tabular-nums">
                {wallet.currency} {wallet.pendingSettlementBalance ?? '0'}
              </p>
              {wallet.nextSettlementAt ? (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Expected in your account by{' '}
                  {new Date(wallet.nextSettlementAt).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </p>
              ) : Number(wallet.pendingSettlementBalance ?? 0) > 0 ? (
                <p className="mt-1 text-[11px] text-muted-foreground">Will drop into Available soon</p>
              ) : null}
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Pending withdrawals</p>
              <p className="mt-1 text-lg font-semibold tabular-nums">
                {wallet.currency} {wallet.pendingWithdrawalAmount}
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Total withdrawn</p>
              <p className="mt-1 text-lg font-semibold tabular-nums">
                {wallet.currency} {wallet.totalWithdrawn}
              </p>
            </div>
          </div>
        ) : null}

        <p className="text-xs text-muted-foreground">
          {stripeReady || paystackReady
            ? 'You will receive an email when a request is approved, declined, or paid. Manage bank setup under '
            : 'When online payouts are not set up, bank transfer may be handled outside Kartly after approval. You will receive an email when a request is approved, declined, or paid. Set up payouts under '}
          <Link to="/payments/stripe" className="text-primary hover:underline">
            Payouts
          </Link>
          .
        </p>
      </Card>

      <Card>
        <CardHeader title="Withdrawal history" description="All requests for the selected store." />
        {listLoading ? (
          <SkeletonListPage toolbarFields={0} columns={6} rows={6} />
        ) : listTotal === 0 ? (
          <EmptyState
            title="No withdrawal requests yet"
            description="When you request a payout from your available wallet balance, it will appear here."
            action={
              storeId ? (
                <Button type="button" size="sm" onClick={() => setModalOpen(true)}>
                  Request withdrawal
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="min-w-full divide-y divide-border text-sm">
                <thead className="bg-muted/30 text-left text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">S/N</th>
                    <th className="px-4 py-3 font-medium">Requested</th>
                    <th className="px-4 py-3 font-medium">#</th>
                    <th className="px-4 py-3 font-medium">Amount</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-card">
                  {withdrawals.map((row, rowIndex) => (
                    <tr key={row.id}>
                      <td className="px-4 py-3 tabular-nums">{rowIndex + 1}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">
                        {formatWhen(row.createdAt)}
                      </td>
                      <td className="px-4 py-3 tabular-nums">{row.id}</td>
                      <td className="px-4 py-3 tabular-nums">
                        {row.currency} {row.amount}
                      </td>
                      <td className="px-4 py-3">{formatWithdrawalStatus(row.status)}</td>
                      <td className="max-w-[16rem] px-4 py-3 text-xs text-muted-foreground">
                        {row.status === 'rejected' && row.rejectionReason?.trim() ? (
                          <span className="whitespace-pre-wrap">{row.rejectionReason}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <LoadMoreFooter
              shown={withdrawals.length}
              total={listTotal}
              hasMore={hasMore}
              loading={listLoading}
              loadingMore={loadingMore}
              onLoadMore={loadMore}
            />
          </>
        )}
      </Card>

      <Modal open={modalOpen} title="Request withdrawal" onClose={closeModal} size="sm">
        <div className="space-y-4 text-foreground">
          <p className="text-sm text-muted-foreground">
            Withdraw from your Available balance. Payments that are still on the way will show up here once
            they’re ready.
          </p>
          <label className="block space-y-1">
            <span className="text-xs text-muted-foreground">Amount ({wallet?.currency ?? '—'})</span>
            <Input
              type="number"
              min={0}
              step="0.01"
              max={wallet ? Number(wallet.availableBalance) : undefined}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="max-w-[14rem]"
              disabled={Number(wallet?.availableBalance ?? 0) <= 0}
            />
          </label>
          {wallet ? (
            <div className="space-y-1 text-xs text-muted-foreground">
              <p>
                Available: {wallet.currency} {wallet.availableBalance}
              </p>
              {Number(wallet.pendingSettlementBalance ?? 0) > 0 ? (
                <p>
                  On the way: {wallet.currency} {wallet.pendingSettlementBalance}
                  {wallet.nextSettlementAt
                    ? ` — expected by ${new Date(wallet.nextSettlementAt).toLocaleString(undefined, {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })}`
                    : ''}
                </p>
              ) : null}
              {Number(wallet.availableBalance) <= 0 && Number(wallet.pendingSettlementBalance ?? 0) > 0 ? (
                <p className="text-amber-700 dark:text-amber-400">
                  Nothing to withdraw yet — funds on the way will drop into Available soon.
                </p>
              ) : null}
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => void submitWithdrawal()}
              disabled={!storeId || submitting || Number(wallet?.availableBalance ?? 0) <= 0}
            >
              {submitting ? 'Submitting…' : 'Submit request'}
            </Button>
            <Button type="button" variant="secondary" onClick={closeModal} disabled={submitting}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
